import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import {
  ClientCategoryDto,
  ClientDto,
} from "../../modules/Admin/ClientManagement/types";
import { Roles } from "../../modules/Account/AccountLogin/types";
import { getActiveUser } from "../../utils/SsoSession";
import { HEADER_THEME } from "../config/theme";
import { REDESIGN_NS } from "../i18n";
import { useCurrentUser } from "../lib/useCurrentUser";
import { fetchClientCategories } from "../lib/clientCategories";
import { subscribePortalData } from "../lib/portalData";
import { fetchUserClients } from "../lib/userClients";
import { useTheme } from "../lib/useTheme";
import { getInitials } from "../ui/avatar";
import { IconChevronDown, IconMenu } from "../ui/icons";
import { useDismissable } from "../ui/useDismissable";
import AccountMenu from "./AccountMenu";
import LanguageMenu from "./LanguageMenu";
import MegaMenu from "./MegaMenu";
import ThemeMenu from "./ThemeMenu";

/** Fare nav öğesiyle panel arasında geçerken menü kapanmasın diye. */
const MEGA_CLOSE_DELAY_MS = 140;

interface NavItem {
  path: string;
  labelKey: string;
  adminOnly: boolean;
  hasMegaMenu?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { path: "/login-as-user", labelKey: "Login As User", adminOnly: true },
  { path: "/client-page", labelKey: "Portal Management", adminOnly: true },
  { path: "/portal-selection", labelKey: "Portal Selection", adminOnly: false, hasMegaMenu: true },
];

const Header: FC = () => {
  const { t } = useTranslation(REDESIGN_NS);
  const { resolved: resolvedTheme } = useTheme();
  const history = useHistory();
  const location = useLocation();

  const [isMegaOpen, setIsMegaOpen] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [clients, setClients] = useState<ClientDto[]>([]);
  const [categories, setCategories] = useState<ClientCategoryDto[]>([]);
  const clientsLoaded = useRef(false);

  const megaTimer = useRef<number | null>(null);
  const accountAnchorRef = useRef<HTMLDivElement>(null);
  const navAnchorRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const megaItemRef = useRef<HTMLButtonElement>(null);
  const megaPanelRef = useRef<HTMLDivElement>(null);
  /** Panelin sağ kenarının header'ın sağ kenarına uzaklığı (px). */
  const [megaRight, setMegaRight] = useState<number | null>(null);

  const activeUser = getActiveUser();
  const profile = useCurrentUser();
  const isAdmin = (activeUser?.userRoles || []).some(
    (role) => role?.toString() === Roles.admin.toString()
  );

  const closeAccount = useCallback(() => setIsAccountOpen(false), []);
  useDismissable(accountAnchorRef, isAccountOpen, closeAccount);

  const closeNav = useCallback(() => setIsNavOpen(false), []);
  useDismissable(navAnchorRef, isNavOpen, closeNav);

  const loadClients = useCallback(async () => {
    if (clientsLoaded.current) return;
    clientsLoaded.current = true;

    // Portal Seçimi ekranı da aynı listeleri istiyor; `fetch*` uçuştaki isteği paylaştırır.
    const [result, categoryList] = await Promise.all([
      fetchUserClients(),
      fetchClientCategories(),
    ]);

    setCategories(categoryList);
    if (result.length > 0) setClients(result);
    // Boş sonuç hata da olabilir; bayrak geri alınır ki sonraki hover yeniden denesin.
    else clientsLoaded.current = false;
  }, []);

  /*
   * Portal listesi mega menü için ÖNCEDEN çekilir. Hover'da çekilirken ilk gelişte panel
   * hiç açılmıyordu: `clients` boşken MegaMenu `null` döner, veri geldiğinde de fare
   * ayrılmışsa kapanma zamanlayıcısı çoktan çalışmış oluyordu.
   */
  useEffect(() => {
    loadClients();
    return subscribePortalData(() => {
      clientsLoaded.current = false;
      loadClients();
    });
  }, [loadClients]);

  const cancelMegaClose = () => {
    if (megaTimer.current !== null) {
      window.clearTimeout(megaTimer.current);
      megaTimer.current = null;
    }
  };

  /*
   * Panel "Portal Seçimi" öğesinin altına ortalanır; CSS ile yapılamıyor çünkü nav esnek
   * ve öğenin konumu sağ gruptaki içeriğe göre kayıyor. Sonuç `right` olarak verilir
   * (panelin CSS dayanağı sağ kenar) ve iki yönde de kelepçelenir.
   */
  const placeMega = useCallback(() => {
    const header = headerRef.current;
    const item = megaItemRef.current;
    const panel = megaPanelRef.current;
    if (!header || !item || !panel) return;

    const headerRect = header.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const pad = parseFloat(getComputedStyle(header).paddingRight) || 0;

    const itemCenter = itemRect.left + itemRect.width / 2;
    const offset = headerRect.right - (itemCenter + panel.offsetWidth / 2);
    const maxOffset = headerRect.width - panel.offsetWidth - pad;

    setMegaRight(Math.max(pad, Math.min(offset, maxOffset)));
  }, []);

  // Boyama öncesi ölçülür ki panel yanlış yerde bir kare görünmesin.
  useLayoutEffect(() => {
    if (!isMegaOpen) return;
    placeMega();
    window.addEventListener("resize", placeMega);
    return () => window.removeEventListener("resize", placeMega);
  }, [isMegaOpen, clients, placeMega]);

  const openMega = () => {
    cancelMegaClose();
    loadClients();
    setIsMegaOpen(true);
  };

  const scheduleMegaClose = () => {
    cancelMegaClose();
    megaTimer.current = window.setTimeout(
      () => setIsMegaOpen(false),
      MEGA_CLOSE_DELAY_MS
    );
  };

  useEffect(() => cancelMegaClose, []);

  const go = (path: string) => {
    setIsMegaOpen(false);
    setIsNavOpen(false);
    if (location.pathname !== path) history.push(path);
  };

  const visibleItems = NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin);
  /* Koyu temada header her zaman koyudur; `HEADER_THEME` yalnızca açık temada söz sahibi. */
  const themeClass =
    resolvedTheme === "dark" || HEADER_THEME === "dark"
      ? "sso-rd__header--dark"
      : "sso-rd__header--light";

  return (
    <header className={"sso-rd__header " + themeClass} ref={headerRef}>
      {/* --- Logo ---
          README "Assets": üretimde kurumsal SVG logo kullanılmalı. Repoda kurumsal
          SVG yok (src/logo.svg CRA varsayılanı, eski header uzak bir URL'den Securitas
          AB logosunu çekiyor). Prototipteki üç daire + kelime markası kurulumu
          uygulandı; kurumsal SVG temin edildiğinde burası tek noktadan değişir. */}
      <button
        type="button"
        className="sso-rd__logo"
        onClick={() => go("/portal-selection")}
        aria-label="Securitas"
      >
        <span className="sso-rd__logo-dots" aria-hidden="true">
          <span className="sso-rd__logo-dot" />
          <span className="sso-rd__logo-dot" />
          <span className="sso-rd__logo-dot" />
        </span>
        <span className="sso-rd__logo-word">Securitas</span>
      </button>

      {/* --- Hamburger ---
          Nav ve bu panel aynı anda DOM'da durur, hangisinin görüneceğine CSS
          karar verir; böylece resize dinleyicisi tutmak gerekmez. */}
      <div className="sso-rd__nav-toggle-anchor" ref={navAnchorRef}>
        <button
          type="button"
          className="sso-rd__nav-toggle"
          onClick={() => setIsNavOpen((open) => !open)}
          aria-haspopup="menu"
          aria-expanded={isNavOpen}
          aria-label={t("Menu")}
        >
          <IconMenu size={20} />
        </button>

        <div
          className={"sso-rd__nav-panel" + (isNavOpen ? " is-open" : "")}
          role="menu"
        >
          {visibleItems.map((item) => (
            <button
              key={item.path}
              type="button"
              role="menuitem"
              className={
                "sso-rd__nav-panel-item" +
                (location.pathname.startsWith(item.path) ? " is-active" : "")
              }
              onClick={() => go(item.path)}
            >
              {t(item.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* --- Nav --- */}
      <nav className="sso-rd__nav">
        {visibleItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          return (
            <button
              key={item.path}
              type="button"
              ref={item.hasMegaMenu ? megaItemRef : undefined}
              className={
                "sso-rd__nav-item" +
                (isActive ? " is-active" : "") +
                (item.hasMegaMenu && isMegaOpen ? " is-mega-open" : "")
              }
              onClick={() => go(item.path)}
              onMouseEnter={item.hasMegaMenu ? openMega : undefined}
              onMouseLeave={item.hasMegaMenu ? scheduleMegaClose : undefined}
            >
              {t(item.labelKey)}
              {/* size: dil seçicideki okla aynı. */}
              {item.hasMegaMenu && (
                <IconChevronDown size={13} className="sso-rd__nav-caret" />
              )}
            </button>
          );
        })}
      </nav>

      {/* --- Sağ grup --- */}
      <div className="sso-rd__header-right">
        <ThemeMenu />
        <LanguageMenu />

        <div className="sso-rd__menu-anchor" ref={accountAnchorRef}>
          <button
            type="button"
            className="sso-rd__avatar-btn"
            onClick={() => setIsAccountOpen((open) => !open)}
            aria-haspopup="menu"
            aria-expanded={isAccountOpen}
            aria-label={t("Profile")}
          >
            {profile?.avatar ? (
              <img src={profile.avatar} alt="" />
            ) : (
              <span className="sso-rd__avatar-initials">
                {getInitials(profile?.name || activeUser?.username)}
              </span>
            )}
          </button>

          <AccountMenu
            isOpen={isAccountOpen}
            onClose={closeAccount}
            profile={profile}
          />
        </div>
      </div>

      {/* Koşulsuz render: kapanış animasyonu ancak DOM'da kalırsa oynayabilir.
          İlk hover'a kadar `clients` boş olduğu için MegaMenu kendisi null döner. */}
      <MegaMenu
        clients={clients}
        categories={categories}
        isOpen={isMegaOpen}
        panelRef={megaPanelRef}
        right={megaRight}
        onMouseEnter={cancelMegaClose}
        onMouseLeave={scheduleMegaClose}
      />
    </header>
  );
};

export default Header;
